<script lang="ts" setup>
import { Category } from '@/models/category.model';
import { ShiftCMSState } from '@/store';
import { CategoriesActions, CategoriesState } from '@/stores/categories.store';
import { ref, computed, onMounted, PropType, watch } from 'vue';
import { useStore } from 'vuex';

const props = defineProps({
  label: {
    type: String,
  },
  modelValue: {
    type: Array as PropType<Category[]>,
    required: true,
    default: () => [],
  },
  required: {
    type: Boolean,
    default: () => false,
  },
  disabled: {
    type: Boolean,
    default: () => false,
  },
  max: {
    type: Number,
    default: 0,
  },
});

const store = useStore<ShiftCMSState & { cs: CategoriesState }>();
const emit = defineEmits(['update:modelValue', 'change']);

const categories = ref<Category[]>([...(props.modelValue || [])]);

function getParentString(c: Category, categories: Category[]): string {
  if (c.parentCategory) {
    const parent = categories.find((p) => p.id === c.parentCategory?.id);

    if (parent) {
      return `${getParentString(parent, categories)}${parent.title} - `;
    }
  }
  return '';
}

const categoryItems = computed(() =>
  [...store.state.cs.categories]
    .sort((a, b) => a.index - b.index)
    .map((c) => {
      return {
        title: `${
          getParentString(c, store.state.cs.categories)
            ? `${getParentString(c, store.state.cs.categories)}`
            : ''
        }${c.title}`,
        value: c,
      };
    }),
);

function update(v: Category[]) {
  emit('update:modelValue', v);
  emit('change');
}

watch(
  () => props.modelValue,
  () => {
    categories.value = [...props.modelValue];
  },
);

onMounted(() => {
  store.dispatch(CategoriesActions.GetCategories);
});
</script>
<template>
  <v-autocomplete
    :label="
      props.label
        ? props.label
        : $t(
            `${
              max !== 1
                ? 'initiative.edit.categories'
                : props.label
                ? props.label
                : 'initiative.edit.category'
            }`,
          )
    "
    v-model="categories"
    :items="categoryItems"
    :disabled="disabled"
    chips
    multiple
    :valueComparator="(a, b) => a.id === b.id"
    @update:modelValue="update"
    ><template v-slot:item="{ props }">
      <v-list-item v-bind="props">
        <v-list-item-title></v-list-item-title>
      </v-list-item>
    </template>
  </v-autocomplete>
  <v-container style="padding-top: 0; padding-bottom: 0">
    <v-alert
      type="warning"
      density="compact"
      v-if="categories?.length > max && max !== 0"
      >Only use one category</v-alert
    >
    <v-alert
      type="warning"
      density="compact"
      v-if="categories?.length === 0 && required"
      >This is required</v-alert
    >
  </v-container>
</template>
